import { FC, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import {
  CardOnboardingResponse,
  CardOnboardingStep,
  useCardIdentificationUrl
} from '../../../../queries/pxtraCard';
import { Button } from '../../atomic/button';
import { FeedbackAlert } from '../../atomic/feedback/alert';
import { ChecklistItem } from './checklistItem';
import { DocumentsBlock } from './documentsBlock';
import { FirstTransferBlock } from './firstTransferBlock';
import { onboardingSeverity } from './onboardingNotice';
import { onboardingSentence, openStepCount } from './sentence';
import { StartOnboardingDialog } from './startDialog';
import { TaxIdBlock } from './taxIdBlock';

type Props = {
  companyId: string;
  onboarding: CardOnboardingResponse;
};

// Steps whose text does not depend on their state.
const FIXED_DESCRIPTIONS: Partial<Record<CardOnboardingStep['id'], string>> = {
  tax_id: 'pxtraCard:step_taxIdDescription',
  first_transfer: 'pxtraCard:step_firstTransferDescription',
  // Swan collects these through its own support rather than a pxtra form,
  // so the step says what is missing and who will come back to you.
  ubo_details: 'pxtraCard:step_uboDetailsDescription',
  legal_representative_details: 'pxtraCard:step_legalRepresentativeDetailsDescription',
  organization_details: 'pxtraCard:step_organizationDetailsDescription',
  other: 'pxtraCard:step_otherDescription'
};

const identificationDescriptionKey = (step: CardOnboardingStep): string => {
  // Somebody else signs: "your identification" and "start again" would
  // address the wrong person, and there is no button here to press.
  const who = step.payload.representation === 'other' ? 'Other' : 'Self';

  if (step.state === 'done') return 'pxtraCard:step_identificationDone';

  // Swan's providers are reviewing what the person already did, so asking
  // them to start again would be wrong.
  if (step.state === 'waiting') return `pxtraCard:step_identificationReviewing${who}`;

  // A failed or expired attempt has to be repeated, and saying so beats an
  // unexplained "start again".
  if (['invalid', 'canceled', 'expired'].includes(step.payload.identification_status ?? ''))
    return `pxtraCard:step_identificationRetry${who}`;

  // Swan's own link is a one-time SMS. Once the form is finished the person
  // may well have missed it, so the text says how to get back in.
  return `pxtraCard:step_identification${who}Open`;
};

const descriptionKey = (step: CardOnboardingStep): string => {
  const fixed = FIXED_DESCRIPTIONS[step.id];

  if (fixed) return fixed;

  switch (step.id) {
    case 'start':
      return step.state === 'done'
        ? 'pxtraCard:step_startDescriptionDone'
        : 'pxtraCard:step_startDescriptionOpen';
    case 'swan_form':
      if (step.state === 'done') return 'pxtraCard:step_swanFormDescriptionDone';

      // Swan withholds the link while public links are off; without this the
      // step would sit there with nothing to press and no reason given.
      return step.payload.link_unavailable
        ? 'pxtraCard:step_swanFormLinkUnavailable'
        : 'pxtraCard:step_swanFormDescriptionOpen';
    case 'identification':
      return identificationDescriptionKey(step);
    case 'documents':
      return step.state === 'waiting'
        ? 'pxtraCard:step_documentsWaiting'
        : 'pxtraCard:step_documentsDescription';
    default:
      if (step.state === 'done') return 'pxtraCard:step_reviewDescriptionDone';

      return step.state === 'waiting'
        ? 'pxtraCard:step_reviewDescriptionOpen'
        : 'pxtraCard:step_reviewDescriptionUpcoming';
  }
};

const descriptionParams = (step: CardOnboardingStep): Record<string, string> => {
  if (step.id === 'start') {
    return {
      date: step.payload.started_at
        ? new Date(step.payload.started_at).toLocaleDateString('de-DE')
        : ''
    };
  }

  if (step.id === 'identification') return { name: step.payload.name ?? '' };

  return {};
};

export const SetupFace: FC<Props> = ({ companyId, onboarding }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [startOpen, setStartOpen] = useState(false);
  const { mutate: identify, isPending: isIdentifying } = useCardIdentificationUrl(companyId);

  const sentence = onboardingSentence(onboarding);
  const openCount = openStepCount(onboarding);
  const notStarted = onboarding.phase === 'not_started';
  const requirement = sentence.requirement
    ? t(`pxtraCard:requirement_${sentence.requirement}`)
    : '';
  const countSuffix = openCount
    ? ` ${t('pxtraCard:onboardingOpenCount', { count: openCount })}`
    : '';

  const description = (step: CardOnboardingStep): string => {
    const key = descriptionKey(step);

    return t(key, descriptionParams(step));
  };

  const action = (step: CardOnboardingStep): ReactNode => {
    if (step.id === 'start' && step.state !== 'done') {
      return (
        <Button size="medium" onClick={() => setStartOpen(true)}>
          {t('pxtraCard:step_startAction')}
        </Button>
      );
    }

    // Only for the manager themselves: Swan runs the identification for
    // whoever signs in, so a button here would send the manager through
    // somebody else's signature.
    if (
      step.id === 'identification' &&
      step.state === 'todo' &&
      step.payload.representation !== 'other'
    ) {
      return (
        <Button
          variant="secondary"
          size="medium"
          className="whitespace-nowrap"
          disabled={isIdentifying}
          onClick={() =>
            identify(undefined, {
              onSuccess: ({ identification_url }) => {
                window.location.href = identification_url;
              },
              onError: () =>
                enqueueSnackbar(t('pxtraCard:onboardingActionFailed'), {
                  variant: 'error'
                })
            })
          }
        >
          {t('pxtraCard:step_identificationAction')}
        </Button>
      );
    }

    // Swan's link is single use and gone once the onboarding is finalized, so
    // the button only exists while there is something to return to.
    if (step.id === 'swan_form' && step.state === 'todo' && step.payload.onboarding_url) {
      return (
        <Button
          variant="secondary"
          size="medium"
          className="whitespace-nowrap"
          // Swan's form is another site, so it opens in its own tab the way
          // LinkButton does elsewhere: the dashboard stays put, and a link
          // Swan refuses shows its error rather than looking like a dead
          // button.
          onClick={() => window.open(step.payload.onboarding_url, '_blank')}
        >
          {t('pxtraCard:step_startActionResume')}
        </Button>
      );
    }

    return undefined;
  };

  // "Bei dir" would name the manager for a signature only somebody else can
  // give, contradicting the text right below it.
  const stateLabel = (step: CardOnboardingStep): string | undefined =>
    step.id === 'identification' &&
    step.state === 'todo' &&
    step.payload.representation === 'other' &&
    step.payload.name
      ? t('pxtraCard:stepStateTodoOther', { name: step.payload.name })
      : undefined;

  const extra = (step: CardOnboardingStep): ReactNode => {
    if (step.id === 'documents' && step.state !== 'upcoming') {
      return (
        <DocumentsBlock companyId={companyId} reviewRequested={!!step.payload.review_requested} />
      );
    }

    if (step.id === 'tax_id' && step.payload.subjects?.length) {
      return <TaxIdBlock companyId={companyId} subjects={step.payload.subjects} />;
    }

    if (step.id === 'first_transfer') {
      return <FirstTransferBlock iban={step.payload.iban} />;
    }

    return null;
  };

  return (
    <>
      {/* Before the start the checklist itself says that nothing has begun, so
          repeating it in an alert adds nothing. */}
      {!notStarted && (
        <FeedbackAlert
          severity={onboardingSeverity(onboarding)}
          bulletPoints={false}
          description={`${t(sentence.key, { requirement })}${countSuffix}`}
        />
      )}

      {notStarted && <p className="mt-4 text-sm text-grey-700">{t('pxtraCard:setupIntro')}</p>}

      <div className="mt-4 flex flex-col gap-3">
        {onboarding.steps.map((step) => (
          <ChecklistItem
            key={step.id}
            state={step.state}
            title={t(`pxtraCard:step_${step.id}`)}
            description={description(step)}
            stateLabel={stateLabel(step)}
            action={action(step)}
          >
            {extra(step)}
          </ChecklistItem>
        ))}
      </div>

      <div className="mt-4">
        <FeedbackAlert
          severity="info"
          bulletPoints={false}
          description={t('pxtraCard:setupParallelHint')}
        />
      </div>

      <StartOnboardingDialog
        companyId={companyId}
        company={onboarding.company}
        open={startOpen}
        onClose={() => setStartOpen(false)}
      />
    </>
  );
};
